/* eslint-disable no-param-reassign */
/* eslint-disable import/prefer-default-export */
import axios, { AxiosInstance, AxiosError } from 'axios';
import Storage, { STORAGE_KEYS } from '@/utils/helpers/Storage';
import RequestQueue from '@/lib/auth/requestQueue';
import { globalLogOut, refreshTokens } from '@/lib/auth/authServices';
import { setCurrentLocationPath } from '@/utils/helpers/authHelpers';

async function refreshAccessToken(
  reqQueue: RequestQueue,
  api: AxiosInstance,
  rejected: AxiosError,
): Promise<unknown> {
  const currentRefreshToken = Storage.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN);
  try {
    const { response: errorResponse } = rejected;
    if (!currentRefreshToken) {
      reqQueue.clearQueue();
      setCurrentLocationPath(window?.location?.pathname);
      await globalLogOut();
      // eslint-disable-next-line @typescript-eslint/return-await
      return Promise.reject(rejected);
    }

    // Build a promise that will be handed to the caller instead of the error.
    const deferredRefresh = new Promise((resolve) => {
      reqQueue.enqueue((token: string) => {
        if (errorResponse) {
          errorResponse.config.headers.Authorization = `Bearer ${token}`;
          resolve(axios(errorResponse.config));
        }
      });
    });

    // Check if we're already trying to fetch a replacement access token.
    if (!reqQueue.isFetchingToken) {
      reqQueue.isFetchingToken = true;

      const data = await refreshTokens(currentRefreshToken);

      const newAccessToken = data?.access_token;

      if (!newAccessToken) {
        // The refresh request failed, reset the queue, sign the
        // user out and return the error.
        reqQueue.clearQueue();
        // Clear the session cookies
        setCurrentLocationPath(window?.location?.pathname);
        await globalLogOut();
        // Forward the rejection
        // eslint-disable-next-line @typescript-eslint/return-await
        return Promise.reject(rejected);
      }

      // The refresh request succeeded, save the token details for future
      // requests and make the queued requests again.
      Storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, newAccessToken);
      api.defaults.headers.Authorization = `Bearer ${newAccessToken}`;

      reqQueue.dequeueAll(newAccessToken);
    }

    // Return the promise, which will fulfill when we have a new token,
    // instead of the error.
    // eslint-disable-next-line @typescript-eslint/return-await
    return deferredRefresh;
  } catch (error) {
    // Something went generically wrong, return this error.
    // console.log('Failed to refresh tokens', error);
    reqQueue.clearQueue();
    setCurrentLocationPath(window?.location?.pathname);
    await globalLogOut();
    return Promise.reject(error);
  }
}

export { refreshAccessToken };
